import React, { useState, useEffect, useCallback, useRef } from 'react';
import DayView from './DayView';
import WeekView from './WeekView';
import { api } from '../../shared/utils/api';
import { subjectBorderClass as subjectBorder, subjectDotColour as subjectDot, subjectAbbrev } from '../../shared/constants/subjects';
import { dateToStr, todayStr, addDays, weekStartOf, formatWeekLabel } from './plannerUtils';

// ─── WeeklySummaryCard ────────────────────────────────────────────────────────

function WeeklySummaryCard({ summary, onDismiss }) {
  if (!summary || summary.topics_completed === 0) return null;
  const { topics_completed, topics_moved, avg_confidence, weakest_subject, weakest_subject_avg } = summary;
  return (
    <div className="planner-summary-card">
      <button className="planner-summary-dismiss" onClick={onDismiss}>×</button>
      <p className="planner-summary-text">
        Last week: <strong>{topics_completed} topics covered</strong>
        {topics_moved > 0 && `, ${topics_moved} moved forward`}.
        {avg_confidence != null && <> Average confidence: <strong>{Math.round(avg_confidence * 100)}%</strong>.</>}
        {weakest_subject && weakest_subject_avg != null && <> Weakest: <strong>{weakest_subject}</strong> ({Math.round(weakest_subject_avg * 100)}%).</>}
      </p>
    </div>
  );
}

// ─── FirstRunBanner ───────────────────────────────────────────────────────────

function FirstRunBanner({ total, onDismiss }) {
  return (
    <div className="planner-first-run-banner">
      <button className="planner-first-run-dismiss" onClick={onDismiss}>×</button>
      <p className="planner-first-run-text">
        Your revision plan is ready. {total} topics, ordered by importance. As you tick them off and add data, the plan will adjust. You can look ahead to any week, but future weeks are projections that will change.
      </p>
    </div>
  );
}

// ─── PlannerPage (was Timetable) ──────────────────────────────────────────────

export default function PlannerPage({ onNavigateToTopic }) {
  const today = todayStr();

  // useState variables:
  // viewMode          — 'week' or 'day', defaults to 'day' on mobile
  // currentWeekStart  — ISO date string for Monday of the displayed week
  // currentDayStr     — ISO date string for the selected day in day view
  // weekData          — API response object for the current week
  // loading           — boolean fetch in progress
  // error             — error string or null
  // progress          — overall progress object { covered, total }
  // summary           — last week summary object or null
  // showSummary       — whether to show WeeklySummaryCard
  // firstRunDismissed — persisted to localStorage
  // showFirstRun      — whether to show FirstRunBanner
  // flkFilter         — 'all', 'FLK1', or 'FLK2'
  // daySort           — 'priority', 'subject', 'lightest', or 'heaviest'
  // hideCompleted     — boolean
  // toggles           — { l2, depth, questions, lastseen }, persisted to localStorage
  // weekScrollLeft    — boolean scroll indicator
  // weekScrollRight   — boolean scroll indicator

  const [viewMode, setViewMode] = useState(() => window.innerWidth < 768 ? 'day' : 'week');
  const [currentWeekStart, setCurrentWeekStart] = useState(weekStartOf(today));
  const [currentDayStr, setCurrentDayStr] = useState(today);
  const [weekData, setWeekData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(null);
  const [summary, setSummary] = useState(null);
  const [showSummary, setShowSummary] = useState(true);
  const [firstRunDismissed, setFirstRunDismissed] = useState(
    () => localStorage.getItem('planner_first_run_dismissed') === '1'
  );
  const [showFirstRun, setShowFirstRun] = useState(false);

  const [flkFilter, setFlkFilter] = useState('all');
  const [daySort, setDaySort] = useState('priority');
  const [hideCompleted, setHideCompleted] = useState(false);

  // Detail toggles — persisted to localStorage
  const [toggles, setToggles] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('planner_toggles') || '{}');
      return {
        l2:        saved.l2        !== undefined ? saved.l2        : true,
        depth:     saved.depth     !== undefined ? saved.depth     : true,
        questions: saved.questions !== undefined ? saved.questions : false,
        lastseen:  saved.lastseen  !== undefined ? saved.lastseen  : false,
      };
    } catch { return { l2: true, depth: true, questions: false, lastseen: false }; }
  });

  const flipToggle = key => setToggles(prev => {
    const next = { ...prev, [key]: !prev[key] };
    try { localStorage.setItem('planner_toggles', JSON.stringify(next)); } catch {}
    return next;
  });

  const weekGridRef = useRef(null);
  const [weekScrollLeft, setWeekScrollLeft] = useState(false);
  const [weekScrollRight, setWeekScrollRight] = useState(false);

  // Auto-advance to next week only when the entire current week is past
  // (threshold 7 = never fires mid-week, so Sunday stays on the current week)
  useEffect(() => {
    const ws = weekStartOf(today);
    let pastCount = 0;
    for (let i = 0; i < 7; i++) { if (addDays(ws, i) < today) pastCount++; }
    if (pastCount >= 7) {
      const nextWs = addDays(ws, 7);
      setCurrentWeekStart(nextWs);
      setCurrentDayStr(nextWs);
    }
  }, []); // eslint-disable-line

  // Force day view on mobile
  useEffect(() => {
    const onResize = () => { if (window.innerWidth < 768) setViewMode('day'); };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []); // eslint-disable-line

  // Week grid scroll button visibility
  const updateWeekScroll = useCallback(() => {
    const el = weekGridRef.current;
    if (!el) return;
    setWeekScrollLeft(el.scrollLeft > 2);
    setWeekScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 2);
  }, []);

  useEffect(() => {
    const el = weekGridRef.current;
    if (!el) return;
    updateWeekScroll();
    el.addEventListener('scroll', updateWeekScroll, { passive: true });
    window.addEventListener('resize', updateWeekScroll, { passive: true });
    return () => {
      el.removeEventListener('scroll', updateWeekScroll);
      window.removeEventListener('resize', updateWeekScroll);
    };
  }, [weekData, updateWeekScroll]);

  const scrollWeekGrid = dir => {
    weekGridRef.current?.scrollBy({ left: dir * 320, behavior: 'smooth' });
  };

  const loadWeek = useCallback(async ws => {
    setLoading(true); setError(null);
    try {
      const res = await api(`/api/planner/week?date=${ws}`);
      if (!res || res.error) { setError((res && res.error) || 'Could not load planner.'); return; }
      setWeekData(res);
      setProgress(res.overall_progress);
      if (res.first_run && !firstRunDismissed) setShowFirstRun(true);
      setCurrentDayStr(prev => {
        const dates = Object.keys(res.days || {}).sort();
        return dates.includes(prev) ? prev : (dates[0] || ws);
      });
    } catch { setError('Could not load planner. Check your connection and try again.'); }
    finally { setLoading(false); }
  }, [firstRunDismissed]);

  useEffect(() => { loadWeek(currentWeekStart); }, [currentWeekStart, loadWeek]);

  useEffect(() => {
    if (weekData?.is_current_week) {
      const prevWeek = addDays(currentWeekStart, -7);
      api(`/api/planner/summary?week_start=${prevWeek}`)
        .then(res => { if (res && !res.error) setSummary(res); })
        .catch(() => {});
    }
  }, [weekData?.is_current_week, currentWeekStart]);

  const handleComplete = useCallback((entryId, conf, newProgress) => {
    setWeekData(prev => {
      if (!prev) return prev;
      const days = { ...prev.days };
      for (const ds of Object.keys(days)) {
        const idx = days[ds].entries.findIndex(e => e.id === entryId);
        if (idx !== -1) {
          const updated = days[ds].entries.map((e, i) =>
            i === idx ? { ...e, status: 'completed', confidence_on_complete: conf } : e
          );
          days[ds] = { ...days[ds], entries: updated, completed_count: updated.filter(e => e.status === 'completed').length };
          break;
        }
      }
      return { ...prev, days };
    });
    if (newProgress) setProgress(newProgress);
  }, []);

  const handleMove = useCallback(async (entryId, newDate) => {
    const res = await api('/api/planner/move', {
      method: 'POST', body: JSON.stringify({ schedule_entry_id: entryId, new_date: newDate }),
    });
    if (res.ok) loadWeek(currentWeekStart);
  }, [currentWeekStart, loadWeek]);

  const handleAddEntry = useCallback((dateStr, newEntry) => {
    setWeekData(prev => {
      if (!prev || !prev.days[dateStr]) return prev;
      const entry = { id: -(Date.now()), ...newEntry, status: 'pending', confidence_on_complete: null };
      return {
        ...prev,
        days: {
          ...prev.days,
          [dateStr]: {
            ...prev.days[dateStr],
            entries: [...prev.days[dateStr].entries, entry],
            total_count: prev.days[dateStr].total_count + 1,
          },
        },
      };
    });
  }, []);

  const goToPrevWeek = () => {
    const ws = addDays(currentWeekStart, -7);
    const wsEnd = addDays(ws, 6);
    setCurrentWeekStart(ws);
    setCurrentDayStr(today >= ws && today <= wsEnd ? today : wsEnd);
  };
  const goToNextWeek = () => {
    const ws = addDays(currentWeekStart, 7);
    const wsEnd = addDays(ws, 6);
    setCurrentWeekStart(ws);
    setCurrentDayStr(today >= ws && today <= wsEnd ? today : ws);
  };
  const goToThisWeek = () => {
    setCurrentWeekStart(weekStartOf(today));
    setCurrentDayStr(today);
  };
  const navigateToDay = dateStr => {
    const ws = weekStartOf(dateStr);
    setCurrentDayStr(dateStr);
    if (ws !== currentWeekStart) setCurrentWeekStart(ws);
  };
  const dismissFirstRun = () => {
    setShowFirstRun(false);
    setFirstRunDismissed(true);
    localStorage.setItem('planner_first_run_dismissed', '1');
  };

  const isCurrentWeek = currentWeekStart === weekStartOf(today);
  const weekDates = weekData ? Object.keys(weekData.days).sort() : [];
  const currentDayData = weekData?.days?.[currentDayStr] || null;
  const pct = progress ? Math.round((progress.covered / progress.total) * 100) : 0;

  return (
    <div className="page-container">
    <div className="planner-page">

      {/* Header */}
      <div className="planner-top">
        <div>
          <div className="planner-title">Planner</div>
          <div className="planner-week-label">Week of {formatWeekLabel(currentWeekStart)}</div>
        </div>
        <div className="planner-top-right">
          <div className="view-toggle">
            <button
              className={`view-btn${viewMode === 'week' ? ' view-btn-active' : ''}`}
              onClick={() => setViewMode('week')}
            >Week</button>
            <button
              className={`view-btn${viewMode === 'day' ? ' view-btn-active' : ''}`}
              onClick={() => setViewMode('day')}
            >Day</button>
          </div>
          <div className="week-nav">
            <button className="week-nav-btn" onClick={goToPrevWeek}>← Prev</button>
            {!isCurrentWeek && (
              <button className="week-nav-btn week-nav-btn-active" onClick={goToThisWeek}>This week</button>
            )}
            <button className="week-nav-btn" onClick={goToNextWeek}>Next →</button>
          </div>
        </div>
      </div>

      {/* First run / summary banners */}
      {showFirstRun && !firstRunDismissed && (
        <FirstRunBanner total={progress?.total || 497} onDismiss={dismissFirstRun} />
      )}
      {weekData?.is_current_week && showSummary && summary && (
        <WeeklySummaryCard summary={summary} onDismiss={() => setShowSummary(false)} />
      )}

      {/* Progress */}
      {progress && (
        <div className="planner-progress">
          <div className="planner-progress-text">{progress.covered} of {progress.total} topics covered ({pct}%)</div>
          <div className="planner-progress-bar"><div className="planner-progress-fill" style={{ width: `${pct}%` }} /></div>
        </div>
      )}

      {/* Feasibility / status banners */}
      {!loading && weekData?.feasibility?.status === 'tight' && (
        <div className="planner-feasibility-banner planner-feasibility-banner--tight">
          {weekData.feasibility.message}{' '}<a href="/settings" className="planner-feasibility-link">Adjust in Settings →</a>
        </div>
      )}
      {!loading && weekData?.feasibility?.status === 'unrealistic' && (
        <div className="planner-feasibility-banner planner-feasibility-banner--unrealistic">
          {weekData.feasibility.message}{' '}<a href="/settings" className="planner-feasibility-link">Adjust in Settings →</a>
        </div>
      )}
      {!loading && weekData?.is_revision_period && (
        <div className="planner-revision-period-bar">Revision period — focusing on your weakest areas</div>
      )}
      {!loading && weekData?.no_exam_date && (
        <div className="planner-no-exam-message">
          Set your exam date in Settings to generate your plan.{' '}
          <a href="/settings" className="planner-feasibility-link">Go to Settings →</a>
        </div>
      )}
      {!loading && weekData?.error_generating && !weekData?.no_exam_date && (
        <div className="planner-error">There was a problem generating your plan. Try regenerating in Settings.</div>
      )}

      {/* FLK filter */}
      {!loading && weekData && (
        <div className="planner-filters">
          {['all', 'FLK1', 'FLK2'].map(f => (
            <button
              key={f}
              className={flkFilter === f ? 'pill-filter-active' : 'pill-filter'}
              onClick={() => setFlkFilter(f)}
            >
              {f === 'all' ? 'All' : f}
            </button>
          ))}
        </div>
      )}

      {/* Detail toggle bar */}
      {!loading && weekData && (
        <div className="toggle-bar">
          <span className="toggle-label">Show:</span>
          {[
            { key: 'l2',        label: 'L2 group' },
            { key: 'depth',     label: 'Depth' },
            { key: 'questions', label: 'Questions' },
            { key: 'lastseen',  label: 'Last seen' },
          ].map(({ key, label }) => (
            <button
              key={key}
              className={`toggle-chip${toggles[key] ? ' toggle-chip-on' : ''}`}
              onClick={() => flipToggle(key)}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      {loading && <div className="planner-loading">Loading your plan</div>}
      {error && <div className="planner-error">{error}</div>}

      {/* Week view */}
      {!loading && weekData && viewMode === 'week' && (
        <WeekView
          weekData={weekData}
          weekDates={weekDates}
          today={today}
          flkFilter={flkFilter}
          toggles={toggles}
          onComplete={handleComplete}
          onMove={handleMove}
          onAddEntry={handleAddEntry}
          onSwitchToDayView={ds => { setCurrentDayStr(ds); setViewMode('day'); }}
          weekGridRef={weekGridRef}
          weekScrollLeft={weekScrollLeft}
          weekScrollRight={weekScrollRight}
          onScrollWeekGrid={scrollWeekGrid}
        />
      )}

      {/* Day view */}
      {!loading && weekData && viewMode === 'day' && (
        <DayView
          dateStr={currentDayStr}
          dayData={currentDayData}
          allWeekDates={weekDates}
          onComplete={handleComplete}
          onMove={handleMove}
          onAddEntry={handleAddEntry}
          isToday={currentDayStr === today}
          isPast={currentDayStr < today}
          onPrev={() => navigateToDay(addDays(currentDayStr, -1))}
          onNext={() => navigateToDay(addDays(currentDayStr, 1))}
          daySort={daySort}
          setDaySort={setDaySort}
          hideCompleted={hideCompleted}
          setHideCompleted={setHideCompleted}
          flkFilter={flkFilter}
          toggles={toggles}
          onNavigateToTopic={onNavigateToTopic}
        />
      )}

      {!loading && !weekData && !error && (
        <div className="planner-empty">No plan data. Your schedule will appear here once generated.</div>
      )}
    </div>
    </div>
  );
}
